import { useEffect } from "react";

/**
 * Inject one or more JSON-LD <script type="application/ld+json"> blocks into
 * <head>. Each call is keyed by `id` so the same block is replaced (not
 * duplicated) on re-render or route change.
 *
 * Pass `null` or `undefined` for `data` to opt out — any previously injected
 * block with the same `id` is removed. Useful for conditionally rendering
 * schema (e.g. FAQPage only when a post declares FAQs).
 */
export const useJsonLd = (id: string, data: unknown) => {
  useEffect(() => {
    const selector = `script[type="application/ld+json"][data-jsonld-id="${id}"]`;
    const existing = document.head.querySelector(selector);

    if (data == null) {
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      return;
    }

    let script = existing as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-jsonld-id", id);
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);

    return () => {
      const stale = document.head.querySelector(selector);
      if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
    };
  }, [id, data]);
};
